import { PathsV1ModelGenerationSearchPostParametersQueryExtend } from "@/api-schema-enums";
import { useModelGenerationSearchQuery } from "@/common/api/model-generation/use-model-generation-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getModelGenerationMergeLabel } from "../_helpers/model-merge-labels";

const PAGE_SIZE = 50;

interface ModelGenerationComboboxProps {
  value: string;
  onChange: (uuid: string, label: string) => void;
  excludeUuid?: string;
  disabled?: boolean;
}

export const ModelGenerationCombobox = ({ value, onChange, excludeUuid, disabled }: ModelGenerationComboboxProps) => {
  const { t, i18n } = useTranslation("common");
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: PAGE_SIZE,
  });
  const { data, isPending } = useModelGenerationSearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
        extend: [
          PathsV1ModelGenerationSearchPostParametersQueryExtend.ModelFamily,
          PathsV1ModelGenerationSearchPostParametersQueryExtend.ModelFamily_Brand,
        ],
      },
      body: {},
    },
  });
  const items = useMemo(() => data?.data ?? [], [data]);
  const options = useMemo<ComboboxOption[]>(
    () =>
      items
        .filter((modelGeneration) => modelGeneration.uuid !== excludeUuid)
        .map((modelGeneration) => ({
          value: modelGeneration.uuid,
          label: getModelGenerationMergeLabel(modelGeneration, i18n.language),
        })),
    [excludeUuid, i18n.language, items],
  );
  const { isLoading, hasMore, remainingCount, onLoadMore, isLoadingMore, onSearchChange } = createPaginationState(
    data?.meta?.total ?? 0,
    items.length,
    isPending,
  );
  const placeholder = t("SearchModels");

  return (
    <Combobox
      options={options}
      value={value}
      onChange={(next) => {
        const selected = items.find((modelGeneration) => modelGeneration.uuid === next);
        onChange(next as string, selected ? getModelGenerationMergeLabel(selected, i18n.language) : "");
      }}
      onSearchChange={onSearchChange}
      shouldFilter={false}
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      hasMore={hasMore}
      remainingCount={remainingCount}
      onLoadMore={onLoadMore}
      placeholder={placeholder}
      searchPlaceholder={placeholder}
      disabled={disabled}
    />
  );
};
